import React, { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./csaApi";
import CsaDraft from "./CsaDraft";
import { exportReportPdf, previewReportPdf } from "./report/exportPdf";
import { exportReportWord } from "./report/exportWord";
import { exportReportExcel } from "./report/exportExcel";
import "./CsaReport.css";

/**
 * The report screen.
 *
 * Deliberately slim. The two existing report modals — HsePreviewReportModal at
 * 10,446 lines and HseSavedReportModal at 12,953 — exist to let an auditor
 * build observations by hand in 33 rich-text editors. That is exactly what the
 * CSA checklist replaces, so cloning them would duplicate 23k lines of the one
 * thing CSA does not need (design §7f).
 *
 * What is left is genuinely small: choose what prints, write six narrative
 * sections, preview, export.
 */

const PRESETS = [
  { key: "findings", label: "Findings only", hint: "No and Partial — the default" },
  { key: "gaps_only", label: "Non-conformances", hint: "No only" },
  { key: "everything", label: "Everything", hint: "All 163, including passes" },
  { key: "none", label: "Clear", hint: "Start from nothing" },
];

const STATUS_ORDER = { No: 0, Partial: 1, Yes: 2, "N/A": 3 };

/**
 * The two layouts (design §7a). Same data, different level of detail — neither
 * is a different report.
 *
 * Excel is absent on purpose: a spreadsheet is always the register, because
 * Elaborate is a print-layout idea that does not translate to rows a client
 * filters and sorts.
 */
const LAYOUT_CHOICES = [
  { key: "standard", label: "Standard", hint: "One row per finding · ~7 a page · long reports" },
  { key: "elaborate", label: "Elaborate", hint: "Each finding in full · ~4 a page · short reports" },
];

export default function CsaReport({ auditId, audit }) {
  const [tab, setTab] = useState("contents");
  const [layout, setLayout] = useState("standard");
  /*
   * Off by default. A recommendation is written as a corrective action, so
   * printed beside a Yes it reads as a criticism of something that passed —
   * which is why the projection never stored one there. Clients who want the
   * standard shown against every question can turn it on.
   */
  const [conformantRecs, setConformantRecs] = useState(false);
  const [contents, setContents] = useState(null);
  const [model, setModel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [preview, setPreview] = useState(null);
  const [issues, setIssues] = useState(null);
  const previewUrl = useRef(null);

  const notReady = !audit || audit.status === "draft";

  const loadContents = useCallback(() => {
    if (notReady) return;
    api
      .getReportContents(auditId)
      .then(setContents)
      .catch((e) => setError(e.message));
  }, [auditId, notReady]);

  const loadModel = useCallback(() => {
    if (notReady) return;
    api
      .getReportModel(auditId, { layout, conformantRecs })
      .then((m) => {
        setModel(m);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [auditId, notReady, layout, conformantRecs]);

  const loadIssues = useCallback(() => {
    if (notReady) return;
    api
      .listReportIssues(auditId)
      .then(setIssues)
      .catch(() => setIssues(null));
  }, [auditId, notReady]);

  useEffect(loadContents, [loadContents]);
  useEffect(loadModel, [loadModel]);
  useEffect(loadIssues, [loadIssues]);

  // Blob URLs are not collected while the document lives.
  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    []
  );

  const flash = (m) => {
    setNote(m);
    setTimeout(() => setNote(null), 4000);
  };

  const applyPreset = async (key) => {
    setBusy(true);
    try {
      const r = await api.setReportContents(auditId, { preset: key });
      flash(`${r.selected} of ${r.total ?? contents.total} will print.`);
      loadContents();
      loadModel();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item) => {
    // Optimistic — a checkbox that waits for a round trip feels broken when
    // you are working down a list of 163.
    setContents((prev) => ({
      ...prev,
      selected: prev.selected + (item.is_selected ? -1 : 1),
      items: prev.items.map((x) =>
        x.id === item.id ? { ...x, is_selected: !x.is_selected } : x
      ),
    }));
    try {
      await api.setReportContents(auditId, { ids: [item.id], is_selected: !item.is_selected });
      loadModel();
    } catch (e) {
      setError(e.message);
      loadContents();
    }
  };

  const saveSection = async (key, body) => {
    try {
      await api.saveReportSection(auditId, key, body);
      loadModel();
    } catch (e) {
      setError(e.message);
    }
  };

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const m = await api.getReportModel(auditId, { layout, conformantRecs });
      const { url, bytes } = await previewReportPdf(m, { photoBytes: api.photoBytes });
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = url;
      setPreview({ url, bytes });
      setTab("preview");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * All three formats are the same call with a different renderer — the model
   * is fetched once and drawn, never rebuilt per format. That is what stops a
   * client's PDF and spreadsheet disagreeing.
   */
  const EXPORTERS = {
    pdf: exportReportPdf,
    word: exportReportWord,
    excel: exportReportExcel,
  };

  /**
   * Exporting opens an issue first.
   *
   * The server allocates the number, freezes what went out, and returns the
   * model with the issue stamped into its header — so the footer the client
   * reads and the record we keep cannot disagree. Drawing a model fetched
   * separately would print a document that no row describes.
   */
  const doExport = async (format) => {
    setBusy(true);
    setError(null);
    try {
      const { issue, model: stamped } = await api.openReportIssue(auditId, {
        file_format: format,
        layout,
        // The export does not send the model on screen — the server builds a
        // fresh one and stamps the issue number into it, so every option the
        // screen offers has to be repeated here.
        conformant_recs: conformantRecs ? 1 : 0,
      });
      const out = await EXPORTERS[format](stamped, { photoBytes: api.photoBytes });
      /*
       * The photo note is appended only when something is missing. A document
       * that looks complete and is not is the failure worth interrupting for.
       */
      flash(`Issue ${issue.issue_no} · ${out.name} — ${Math.round(out.bytes / 1024)} KB` +
            (out.photos ? ` · ${out.photos}` : ""));
      loadIssues();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (notReady) {
    return (
      <div className="cap-writeup">
        <div className="wu-empty">
          <h2>No report yet</h2>
          <p>
            The report is built from the submitted audit. Finish and submit first — the
            score is fixed at that moment, and everything the client sees comes from it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cap-writeup rep">
      <div className="wu-head">
        <div>
          <h2>Report</h2>
          <p>
            {model
              ? `${model.summary.selected} of ${model.summary.counts.total} findings will print · ${model.layout} layout`
              : "Loading…"}
          </p>
        </div>
        <div className="rep-actions">
          <button type="button" className="small-btn" disabled={busy} onClick={doPreview}>
            {busy ? "Working…" : "Preview"}
          </button>
          <button
            type="button"
            className="cap-btn"
            disabled={busy || !model}
            onClick={() => doExport("pdf")}
          >
            PDF
          </button>
          <button
            type="button"
            className="small-btn"
            disabled={busy || !model}
            onClick={() => doExport("word")}
            title="Editable, for pasting into a corrective-action tracker"
          >
            Word
          </button>
          <button
            type="button"
            className="small-btn"
            disabled={busy || !model}
            onClick={() => doExport("excel")}
            title="The register, filterable and sortable"
          >
            Excel
          </button>
        </div>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}
      {note ? <p className="rep-note">{note}</p> : null}

      <div className="rep-layouts">
        {LAYOUT_CHOICES.map((l) => (
          <button
            key={l.key}
            type="button"
            className={"rep-layout" + (layout === l.key ? " on" : "")}
            onClick={() => setLayout(l.key)}
          >
            <strong>{l.label}</strong>
            <span>{l.hint}</span>
          </button>
        ))}
      </div>

      <label className="rep-opt">
        <input
          type="checkbox"
          checked={conformantRecs}
          onChange={(e) => setConformantRecs(e.target.checked)}
        />
        <span>
          <strong>Show recommendations against conformant findings</strong>
          <em>
            Off by default. A recommendation is written as a corrective action, so beside a
            Yes it reads as a fault rather than the standard being met. Turn this on where a
            client wants the requirement shown against every question.
          </em>
        </span>
      </label>

      <nav className="rep-tabs">
        {[
          { k: "contents", label: "What prints" },
          { k: "sections", label: "Narrative" },
          { k: "draft", label: "Drafting" },
          { k: "preview", label: "Preview" },
          { k: "issues", label: issues && issues.total ? `Issues (${issues.total})` : "Issues" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            className={"rep-tab" + (tab === t.k ? " on" : "")}
            onClick={() => setTab(t.k)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "contents" ? (
        <Contents
          contents={contents}
          busy={busy}
          onPreset={applyPreset}
          onToggle={toggle}
        />
      ) : null}

      {tab === "sections" ? <Sections model={model} onSave={saveSection} /> : null}

      {tab === "draft" ? <CsaDraft auditId={auditId} audit={audit} /> : null}

      {tab === "issues" ? <Issues issues={issues} /> : null}

      {tab === "preview" ? (
        preview ? (
          <div className="rep-preview">
            <p className="rep-note">
              {Math.round(preview.bytes / 1024)} KB. A preview is not an issue — it carries no
              issue number and nothing is recorded. Use PDF, Word or Excel to issue it.
            </p>
            <iframe title="Report preview" src={preview.url} />
          </div>
        ) : (
          <div className="wu-empty">
            <h2>Nothing rendered yet</h2>
            <p>Choose what prints, then press Preview.</p>
          </div>
        )
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Contents({ contents, busy, onPreset, onToggle }) {
  const [filter, setFilter] = useState("all");

  if (!contents) return <div className="cap-loading">Loading…</div>;

  const items = contents.items
    .filter((i) =>
      filter === "all" ? true : filter === "in" ? i.is_selected : !i.is_selected
    )
    .slice()
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || a.sr_no - b.sr_no);

  return (
    <>
      <div className="rep-presets">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="rep-preset"
            disabled={busy}
            onClick={() => onPreset(p.key)}
          >
            <strong>{p.label}</strong>
            <span>{p.hint}</span>
          </button>
        ))}
      </div>

      <div className="rep-bar">
        <span>
          <strong>{contents.selected}</strong> of {contents.total} selected
        </span>
        <div className="rep-filters">
          {[
            { k: "all", label: "All" },
            { k: "in", label: "Printing" },
            { k: "out", label: "Excluded" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              className={"rep-filter" + (filter === f.k ? " on" : "")}
              onClick={() => setFilter(f.k)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rep-list">
        {items.map((i) => (
          <label key={i.id} className={"rep-item" + (i.is_selected ? " on" : "")}>
            <input type="checkbox" checked={i.is_selected} onChange={() => onToggle(i)} />
            <span className={"rep-status s-" + String(i.status).replace("/", "")}>{i.status}</span>
            <span className="rep-where">
              {i.pillar} · {i.sub_pillar}
            </span>
            <span className="rep-obs">{i.observation}</span>
            {i.has_photos ? <span className="rep-cam">photo</span> : null}
          </label>
        ))}
        {items.length === 0 ? <p className="rep-none">Nothing here.</p> : null}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function Sections({ model, onSave }) {
  if (!model) return <div className="cap-loading">Loading…</div>;
  const narrative = model.sections.filter((s) => s.kind === "narrative");

  return (
    <div className="rep-sections">
      <p className="rep-hint">
        Sections 3, 4 and 5 are generated from the audit and cannot be edited — a report
        should not be able to state a figure the data does not support.
      </p>
      {narrative.map((s) => (
        <SectionEditor key={s.key} section={s} onSave={onSave} />
      ))}
    </div>
  );
}

function SectionEditor({ section, onSave }) {
  const [text, setText] = useState(section.body || "");
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (!dirty.current) setText(section.body || "");
  }, [section.body]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const type = (e) => {
    const v = e.target.value;
    setText(v);
    dirty.current = true;
    setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      dirty.current = false;
      onSave(section.key, v);
      setSaved(true);
    }, 900);
  };

  return (
    <div className="rep-section">
      <div className="rep-section-head">
        <h3>
          {section.no}. {section.title}
        </h3>
        {saved ? <span className="rep-saved">Saved</span> : null}
      </div>
      <textarea
        rows={6}
        value={text}
        placeholder="Leave blank to omit this section's text. Separate paragraphs with a blank line."
        onChange={type}
        onBlur={() => {
          if (!dirty.current) return;
          clearTimeout(timer.current);
          dirty.current = false;
          onSave(section.key, text);
          setSaved(true);
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * What has actually gone out (design §7b).
 *
 * Selection stays editable after a report is issued, so the same audit can
 * produce several documents. This is how someone answers "which one are they
 * holding?" — and why the checksum is shown: it is over the composed model,
 * not the file, so two exports of identical content match even though their
 * bytes differ.
 */
function Issues({ issues }) {
  if (!issues) return <div className="cap-loading">Loading…</div>;

  if (!issues.total) {
    return (
      <div className="wu-empty">
        <h2>Nothing issued yet</h2>
        <p>
          Every export records an issue — a number, a date, and exactly what was in it. The
          document carries the same line in its footer, so a client can always say which
          version they have.
        </p>
      </div>
    );
  }

  return (
    <div className="rep-issues">
      <p className="rep-hint">
        Scores never change between issues — they were fixed when the audit was submitted.
        Only the level of detail and the selection differ.
      </p>

      {issues.issues.map((i) => (
        <div className="rep-issue" key={i.issue_no}>
          <div className="rep-issue-no">{i.issue_no}</div>
          <div className="rep-issue-main">
            <div className="rep-issue-top">
              <strong>
                {new Date(i.exported_at).toLocaleString(undefined, {
                  day: "numeric", month: "long", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </strong>
              <span className="rep-issue-fmt">{i.file_format}</span>
              <span className="rep-issue-fmt">{i.layout}</span>
            </div>
            <div className="rep-issue-sub">
              {i.selected_count} of {i.assessed_count} assessed findings reported
              {i.exported_by ? ` · ${i.exported_by}` : ""}
            </div>
            <div className="rep-issue-sum" title="Fingerprint of the content, not the file">
              {String(i.checksum || "").slice(0, 16)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
